import {
  FeatureFlags,
  type AmplifyKeys,
  type QuickLoginMethods,
  type QuickLoginRow,
  type QuickLoginUnlockResult,
} from "@observe-tech/utils";
import { createBridgeClient } from "./electronBridge";

const featureFlags = new FeatureFlags();

const QUICK_LOGIN_METHOD_NAMES = [
  "isQuickLoginAvailable",
  "listQuickLogins",
  "rememberQuickLogin",
  "setQuickLoginPin",
  "unlockQuickLogin",
  "removeQuickLoginPin",
  "declineQuickLogin",
  "forgetQuickLogin",
] as const satisfies readonly (keyof QuickLoginMethods)[];

const quickLoginBridge = createBridgeClient<QuickLoginMethods>(
  "quickLoginSession",
  QUICK_LOGIN_METHOD_NAMES,
);

export function isQuickLoginCapable(): boolean {
  return (
    quickLoginBridge.hasAllMethods() &&
    featureFlags.isFeatureEnabled("FEATUREFLAG_QUICK_LOGIN")
  );
}

export function listQuickLogins(): Promise<QuickLoginRow[]> {
  return quickLoginBridge.call<QuickLoginRow[]>(
    "listQuickLogins",
    (bridge) => bridge.listQuickLogins?.(),
    [],
  );
}

export function rememberQuickLogin(
  cognitoSub: string,
  email: string,
  keys: AmplifyKeys,
  expiresAt: number,
): Promise<boolean> {
  return quickLoginBridge.call(
    "rememberQuickLogin",
    (bridge) => bridge.rememberQuickLogin?.(cognitoSub, email, keys, expiresAt),
    false,
  );
}

export function setQuickLoginPin(
  cognitoSub: string,
  pin: string,
  keys: AmplifyKeys,
  expiresAt: number,
): Promise<boolean> {
  return quickLoginBridge.call(
    "setQuickLoginPin",
    (bridge) => bridge.setQuickLoginPin?.(cognitoSub, pin, keys, expiresAt),
    false,
  );
}

export function unlockQuickLogin(
  cognitoSub: string,
  pin: string,
): Promise<QuickLoginUnlockResult> {
  return quickLoginBridge.call<QuickLoginUnlockResult>(
    "unlockQuickLogin",
    (bridge) => bridge.unlockQuickLogin?.(cognitoSub, pin),
    { status: "notFound" },
  );
}

export function forgetQuickLogin(
  cognitoSub: string,
): Promise<AmplifyKeys | null> {
  return quickLoginBridge.call<AmplifyKeys | null>(
    "forgetQuickLogin",
    (bridge) => bridge.forgetQuickLogin?.(cognitoSub),
    null,
  );
}
